import * as React from "react";

import type {GAEModule} from "@khan/data-gql-core";

import {ApolloRouterContext} from "./apollo-router-context.ts";
import {getUnsafeApolloGqlClient} from "./get-unsafe-apollo-gql-client.ts";

interface Props {
    /**
     * The children to render within this component.
     */
    children: React.ReactNode;
    /**
     * The GAE module to use for GraphQL requests by children of this
     * component (until another ApolloRouter overrides it).
     * Defaults to `"default"`.
     */
    module?: GAEModule;
}

/**
 * Configure GraphQL routing for Apollo-based GraphQL operations.
 *
 * This sets up the client for routing GraphQL mutations and queries to the
 * appropriate module. All Apollo operations will use the given routing
 * until a nested router changes that.
 */
export const ApolloRouter = ({children, module}: Props): React.ReactElement => {
    /**
     * Here we get the current context so that we can re-use the client
     * assigned in any parent context. This allows mocks to propagate from
     * a test harness all the way down, even if a new router is used to change
     * the module.
     *
     * However, we always reset the `module` since that feels more expected for
     * devs that if they declare a new router, it's defaults are deterministic.
     * And there is little point in inheriting the `module` since that's the
     * only reason for specifying a `ApolloRouter` anyway.
     */
    const {client: currentClient} = React.useContext(ApolloRouterContext);

    const thisModule = module || "default";

    // We want to always use the same objects if things haven't changed to avoid
    // over-rendering consumers of our context, let's memoize the configuration.
    // By doing this, if a component under children that uses this context
    // uses React.memo, we won't force it to re-render every time we render
    // because we'll only change the context values if something has actually
    // changed.
    const thisApolloContext = React.useMemo(
        () => ({
            module: thisModule,

            /*
             eslint-disable-next-line no-restricted-syntax
             --
             NOTE(somewhatabstract): This is the one place where
             getUnsafeApolloGqlClient() is "safe". It is used to generate
             the root level client used by all subsequent uses unless
             our testing framework overrides it.
             */
            client: currentClient ?? getUnsafeApolloGqlClient(),
        }),
        [thisModule, currentClient],
    );

    return (
        <ApolloRouterContext.Provider value={thisApolloContext}>
            {children}
        </ApolloRouterContext.Provider>
    );
};
